import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { usePreferences } from "@/lib/preferences";
import { cn } from "@/lib/utils";
import { LANDING_COPY } from "./copy";

/**
 * Shared landing primitives (Phase 56 split of the former Sections.js). Everything here is presentation-only and scoped
 * to the `.lp` tree; the signed-in app never imports from this folder.
 */

/**
 * The produced explainer videos are being replaced. Until the new EN/AR cuts are published the section, its nav
 * item and the hero "watch" link stay hidden (flip to true to bring all three back — the copy keys are kept).
 */
export const SHOW_EXPLAINER = false;

/** The live-stats strip only shows once the platform has genuine scale (committee R-3). */
export const STATS_THRESHOLDS = { pools_total: 25, owners: 100 };

export const useCopy = () => {
  const { lang } = useLang();
  return { c: LANDING_COPY[lang], lang, ar: lang === "ar" };
};

export function Container({ className, children }) {
  return <div className={cn("mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8", className)}>{children}</div>;
}

/** Eyebrow (quiet, tracked, with a short brand rule) + display title + optional descriptor. */
export function SectionHeading({ eyebrow, title, subtitle, align = "start", className, id }) {
  return (
    <div className={cn("max-w-3xl", align === "center" && "mx-auto text-center", className)}>
      {eyebrow && (
        <div className={cn("lp-eyebrow mb-4 flex items-center gap-3 text-muted-foreground", align === "center" && "justify-center")}>
          <span className="h-px w-6 bg-primary" aria-hidden />{eyebrow}
        </div>
      )}
      <h2 id={id} className="lp-display text-2xl font-semibold leading-tight tracking-[-0.01em] sm:text-3xl lg:text-4xl">{title}</h2>
      {subtitle && <p className="lp-body mt-4 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">{subtitle}</p>}
    </div>
  );
}

/**
 * Scroll-in reveal without framer-motion (Phase 56, R-5: keeps ~35 kB gz out of the landing chunk). Elements start
 * translated 8 px down and transparent (`.lp-reveal`), and get `.is-in` once they enter the viewport; the CSS transition only
 * touches transform and opacity, and is disabled under prefers-reduced-motion. Children render immediately for crawlers.
 */
export function Reveal({ children, delay = 0, className, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setShown(true); return undefined; }
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { setShown(true); io.disconnect(); } }, { rootMargin: "-60px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={cn("lp-reveal", shown && "is-in", className)} style={delay ? { transitionDelay: `${delay}s` } : undefined} {...rest}>{children}</Tag>;
}

export function useStats() {
  return useQuery({ queryKey: ["public-stats"], queryFn: async () => (await api.get("/public/stats")).data, staleTime: 60_000, retry: 1 });
}

/** Animated number; the currency form follows the interface language (SAR 1,000 / 1,000 ر.س). */
export function Counter({ value, currency = false, ar = false }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; let start;
    const step = (ts) => { if (!start) start = ts; const p = Math.min(1, (ts - start) / 1400); setV(value * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  const n = Math.round(v).toLocaleString("en-US");
  if (!currency) return <span dir="ltr" className="tabular-nums">{n}</span>;
  return <span dir={ar ? "rtl" : "ltr"} className="tabular-nums">{ar ? `${n} ر.س` : `SAR ${n}`}</span>;
}

/** Icon inside the 40px accent tile used across the landing cards. */
export function IconTile({ icon: Icon, className, size = 18 }) {
  return <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground", className)}><Icon size={size} strokeWidth={1.75} /></span>;
}

/** Live pulse dot (still when the user prefers reduced motion). */
export function LiveDot() {
  return <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 motion-safe:animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-primary" /></span>;
}

/** Short "nothing to lose" line under a primary call to action (committee R-8). */
export function Reducers({ items, className, testId }) {
  return (
    <ul className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:text-sm", className)} data-testid={testId}>
      {items.map((r, i) => <li key={r} className="flex items-center gap-3">{i > 0 && <span className="h-1 w-1 rounded-full bg-border" aria-hidden />}{r}</li>)}
    </ul>
  );
}

/**
 * A real product screen (committee R-7): `public/landing/{screen}-{lang}-{theme}.webp`, 1200 × 750, captured from the
 * showcase holding. The variant follows the interface language and the resolved theme so the frame always matches the page.
 * `priority` marks the hero frame (eager + high fetch priority: it is the LCP element); everything else lazy-loads.
 */
export function ProductFrame({ screen, alt, caption, badge, priority = false, className }) {
  const { lang } = useLang();
  const { resolvedTheme } = usePreferences();
  const src = `/landing/${screen}-${lang}-${resolvedTheme === "dark" ? "dark" : "light"}.webp`;
  return (
    <figure className={cn("lp-frame relative", className)} data-testid={`landing-frame-${screen}`}>
      <div className="lp-frame-bar flex items-center gap-1.5 px-3" aria-hidden>
        <span /><span /><span />
        {badge && <span className="ms-auto rounded-full border border-border bg-card px-2 py-0.5 text-2xs font-semibold text-muted-foreground">{badge}</span>}
      </div>
      <img
        src={src} alt={alt} width={1200} height={750}
        loading={priority ? "eager" : "lazy"} decoding="async" fetchPriority={priority ? "high" : "auto"}
        className="block aspect-[8/5] w-full bg-surface-2 object-cover object-top"
        data-testid={`landing-frame-${screen}-image`}
      />
      {caption && <figcaption className="px-3 py-2 text-xs leading-relaxed text-muted-foreground" data-testid={`landing-frame-${screen}-caption`}>{caption}</figcaption>}
    </figure>
  );
}

/** Internal link helper: `to` → router link, `href` → in-page anchor, neither → plain span. */
export function SmartLink({ to, href, className, children, ...rest }) {
  if (to) return <Link to={to} className={className} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={className} {...rest}>{children}</a>;
  return <span className={className} {...rest}>{children}</span>;
}
